import {
  Button,
  ChevronIcon,
  ErrorIcon,
  FilterIcon,
  MagnifierIcon,
  resolutions,
} from '@native-instruments/webex-ni';

import clsx from 'clsx';
import { pushDataEvent } from 'dotcom-core/src/helpers/tracking';
import { useOnClickOutside } from 'dotcom-core/src/hooks/useOnClickOutside';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLoaderData, useSearchParams } from 'react-router';
import type { SortValue } from '~/helpers/algolia';
import { getLoginUrl } from '~/helpers/auth';
import { useIntl } from '~/helpers/intl';
import { useCurrentUrl } from '~/hooks/useCurrentUrl';
import { mobileModalRootId } from '~/root';
import { CurrentFilterTitle } from './FilterMenu';
import { type CatalogLoader, makeSafeSearchQueryParams } from './route';

export function SearchBar() {
  const { totalHits } = useLoaderData<CatalogLoader>();
  const search = useSearch();

  const [sortDropdown, setSortDropdown] = useState(false);
  const [openStickyModal, setOpenStickyModal] = useState(false);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const width = document.body.clientWidth ?? 0;
      const isMobile = width <= resolutions.lg;

      if (!isMobile) {
        setOpenStickyModal(false);
      }
    });

    resizeObserver.observe(document.body);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div className="pb-6 flex flex-col justify-between 2xl:flex-row">
      <div>
        <h2
          className="webex-h3 font-[NativePro] inline-block"
          data-testid="category-title"
        >
          <CurrentFilterTitle />
        </h2>
        <span
          data-testid="product-count"
          className="hidden md:inline text-sm text-grey-60 ms-3"
        >
          {totalHits === 1 ? '1 result' : `${totalHits} results`}
        </span>
      </div>
      <div className="flex justify-between mt-2 2xl:mt-0">
        <div className="flex-1 mr-3 lg:mr-4">
          <SearchInput setQuery={search.setQuery} query={search.query} />
        </div>
        <div className="block lg:hidden">
          <button
            className={clsx(
              'bg-grey-20 rounded-full p-0.75 cursor-pointer',
              openStickyModal && 'hidden',
            )}
            onClick={() => setOpenStickyModal(true)}
          >
            <FilterIcon />
          </button>

          {openStickyModal && (
            <StickyMobileFilter
              hideModal={() => setOpenStickyModal(false)}
              defaultSortBy={search.sort}
              defaultHideProductYouOwn={search.hideProductsYouOwn}
              handleConfirmation={({ sortBy, hideProductsYouOwn }) => {
                search.setSort(sortBy);
                search.setHideProductsYouOwn(hideProductsYouOwn);
              }}
            />
          )}
        </div>

        <div className="hidden lg:block">
          {!openStickyModal && (
            <div className="flex items-center">
              <ToggleProductYouOwn
                value={search.hideProductsYouOwn}
                onToggle={() =>
                  search.setHideProductsYouOwn(!search.hideProductsYouOwn)
                }
              />
              <SortBy
                setSort={search.setSort}
                sort={search.sort}
                setOpen={setSortDropdown}
                open={sortDropdown}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ToggleProductYouOwn({
  value,
  onToggle,
  disabled = false,
}: {
  disabled?: boolean;
  value: boolean;
  onToggle: () => void;
}) {
  const { currentUrl } = useCurrentUrl();
  const { isAuthenticated } = useLoaderData<CatalogLoader>();
  const { t } = useIntl();

  const loginUrl = useMemo(() => {
    const returnTo = new URL(currentUrl);

    returnTo.searchParams.set('hideProductsYouOwn', 'true');

    return getLoginUrl({ returnTo: returnTo.href });
  }, [currentUrl]);

  const Component = useMemo(
    () => (
      <div className="text-xs text-grey-90 flex items-center mr-4">
        <label
          data-testid={
            isAuthenticated ? 'hide-toggle-enabled' : 'hide-toggle-disabled'
          }
          className={clsx(
            'outline-outline-offset-0',
            'group relative transition-[outline-color] ease-in-out cursor-pointer',
            'h-3 w-6 p-0.5 outline rounded-full flex items-center mr-1',
            disabled
              ? 'outline-2 outline-grey-70'
              : !value && 'hover:outline-grey-70 hover:outline-2',
            value
              ? 'outline-1 outline-grey-90 bg-grey-90 hover:outline-2 hover:outline-grey-90'
              : 'outline-grey-60 outline-1',
          )}
        >
          {isAuthenticated && (
            <input
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();

                onToggle();
              }}
              type="checkbox"
              className="hidden"
            />
          )}
          <span
            className={clsx(
              'absolute w-2 h-2 rounded-full bg-grey-60',
              !disabled &&
                (value
                  ? 'bg-white right-0.5'
                  : 'left-0.5 group-hover:bg-grey-60'),
            )}
          />
        </label>
        {t('catalog.ownedProducts.hide', { fallback: 'Hide owned products' })}
      </div>
    ),
    [isAuthenticated, onToggle, t, disabled, value],
  );

  return isAuthenticated ? Component : <a href={loginUrl}>{Component}</a>;
}

type SortOption = {
  value: SortValue;
  translation_key: string;
  fallback: string;
};

const options: SortOption[] = [
  {
    value: 'relevance',
    translation_key: 'catalog.sortRelevant',
    fallback: 'Most relevant',
  },
  {
    value: 'release_date_desc',
    translation_key: 'catalog.sortRecent',
    fallback: 'Most recent',
  },
  {
    value: 'price_asc',
    translation_key: 'catalog.sortLowprice',
    fallback: 'Lowest price',
  },
  {
    value: 'price_desc',
    translation_key: 'catalog.sortHighprice',
    fallback: 'Highest price',
  },
];

function SortBy({
  setOpen,
  open,
  setSort,
  sort,
}: Pick<UseSearch, 'setSort' | 'sort'> & {
  setOpen: (value: boolean) => void;
  open: boolean;
}) {
  const { t } = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const currentOptionValue = sort || 'relevance';
  const currentOption: SortOption = useMemo(() => {
    return options.find((o) => o.value === currentOptionValue) ?? options[0];
  }, [currentOptionValue]);

  useOnClickOutside(ref, () => setOpen(false));

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="sort-dropdown"
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          'transition-[outline-color] ease-in-out w-full min-w-[180px]',
          'flex items-center justify-between h-[40px] outline-solid bg-white',
          'outline outline-offset-0 outline-grey-50 outline-1',
          'text-grey-60 rounded-full bg-transparent px-3 py-1 text-xs',
          'hover:outline-2 hover:text-grey-90 hover:outline-grey-70',
          open && 'outline-2 text-grey-90 outline-grey-90',
        )}
      >
        {t(currentOption.translation_key, { fallback: currentOption.fallback })}
        <ChevronIcon className={clsx('ml-2 w-2', !open && 'rotate-180')} />
      </button>

      {open && (
        <div className="bg-white shadow-lg mt-1 rounded absolute right-0 w-full z-50">
          {options.map((option, index) => (
            <button
              type="button"
              data-testid={`${option.value}`}
              key={option.value}
              onClick={() => {
                setOpen(false);
                setSort(option.value);
              }}
              className={clsx(
                'text-left',
                'transition-[background-color] ease-in-out',
                index === 0 && 'rounded-t',
                index === options.length - 1 && 'rounded-b',
                'cursor-pointer w-full text-xs px-3 py-2 inline-block',
                'hover:bg-grey-20',
                option.value === currentOptionValue && 'bg-grey-20',
              )}
            >
              {t(option.translation_key, { fallback: option.fallback })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SearchInput({
  setQuery,
  query,
}: Pick<UseSearch, 'setQuery' | 'query'>) {
  const { t } = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const placeholderContent = t('components.ProductSearch.placeholder', {
    fallback: 'Search for products',
  });
  const [placeholder, setPlaceholder] = useState(placeholderContent);

  const [value, setValue] = useState<string>(query ?? '');

  const onChangeValue = (newValue: string) => {
    setValue(newValue);
    setQuery(newValue);
  };

  return (
    <div
      className={clsx(
        'relative lg:w-[320px] h-[40px] bg-grey-20 text-grey-90 rounded-full flex items-center justify-between transition-[color,background-color,outline-color] ease-in-out',
        'outline outline-offset-0 outline-grey-20 outline-1',
        'hover:outline-2 hover:outline-grey-70 focus:outline-none focus-within:outline-grey-90 focus-within:outline-2 focus-within:bg-transparent ',
      )}
    >
      <label className="sr-only" htmlFor="search-input">
        {/* catalog.productSearch is currently "Filter by name..." which is */}
        {/* no longer accurate for v2, so going with placeholder content */}
        {placeholderContent}
      </label>
      <input
        className="absolute inset-0 px-5 text-xs text-inherit bg-transparent appearance-none z-0 focus:outline-none placeholder:text-grey-60 [&::-webkit-search-cancel-button]:hidden"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        type="search"
        onChange={(event) => {
          onChangeValue(event.target.value);
        }}
        value={value}
        onFocus={() => setPlaceholder('')}
        onBlur={() => setPlaceholder(placeholderContent)}
        placeholder={placeholder}
        ref={inputRef}
        name="search-input"
        id="search-input"
        data-testid="search-input"
      />
      <MagnifierIcon className="w-2 p-0.25 shrink-0 grow-0 ms-2 z-10" />
      {value && (
        <button
          onClick={() => {
            onChangeValue('');
            inputRef.current?.focus();
          }}
          className="shrink-0 grow-0 me-2 z-10"
          type="reset"
          title={t('catalog.productSearchClearQuery', {
            fallback: 'Clear the search query',
          })}
        >
          <SearchResetIcon />
        </button>
      )}
    </div>
  );
}

function StickyMobileFilter({
  hideModal,
  handleConfirmation,
  defaultSortBy,
  defaultHideProductYouOwn,
}: {
  hideModal: () => void;
  defaultSortBy: SortValue;
  defaultHideProductYouOwn: boolean;
  handleConfirmation: ({
    sortBy,
    hideProductsYouOwn,
  }: {
    sortBy: SortValue;
    hideProductsYouOwn: boolean;
  }) => void;
}) {
  const ref = document.getElementById(mobileModalRootId) as HTMLElement;

  const [hideProductsYouOwn, setHideProductsYouOwn] = useState(
    defaultHideProductYouOwn,
  );

  const [sortBy, setSortBy] = useState<SortValue>(defaultSortBy);
  const [sortDropdown, setSortDropdown] = useState(false);

  return createPortal(
    <div className="shadow-md px-3 py-2 bg-white absolute top-[60px] left-0 right-0 z-50 lg:hidden">
      <div className="flex items-center justify-between w-full">
        <div className="flex-1 mr-3">
          <SortBy
            open={sortDropdown}
            setOpen={setSortDropdown}
            sort={sortBy}
            setSort={setSortBy}
          />
        </div>
        <Button variant="ghost" onClick={hideModal}>
          Cancel
        </Button>
      </div>
      <div className="py-4">
        <ToggleProductYouOwn
          value={hideProductsYouOwn}
          onToggle={() => {
            setHideProductsYouOwn(!hideProductsYouOwn);
          }}
        />
      </div>
      <Button
        fullWidth={true}
        variant="primary"
        onClick={() => {
          handleConfirmation({ sortBy, hideProductsYouOwn });
          hideModal();
        }}
      >
        Confirmation
      </Button>
    </div>,
    ref,
  );
}

function SearchResetIcon() {
  return (
    <ErrorIcon
      className="transition-[color] ease-in-out size-2.5 text-grey-60 hover:text-grey-90"
      data-testid="search-clear-button"
    />
  );
}

type UseSearch = {
  query?: string;
  hideProductsYouOwn: boolean;
  setHideProductsYouOwn: (val: boolean) => void;
  sort: SortValue;
  setQuery: (query?: string) => void;
  setSort: (sort: SortValue) => void;
};

function useSearch(): UseSearch {
  const [searchParams, setSearchParams] = useSearchParams();
  const safeParams = makeSafeSearchQueryParams(searchParams);

  return {
    query: safeParams.query,
    sort: safeParams.sort ?? 'relevance',
    hideProductsYouOwn: safeParams.hideProductsYouOwn ?? false,
    setHideProductsYouOwn: (value?: boolean) => {
      if (value) {
        searchParams.set('hideProductsYouOwn', 'true');
      } else {
        searchParams.delete('hideProductsYouOwn');
      }

      pushDataEvent({
        action: value ? 'hide' : 'show',
        category: 'owned-products-filter',
        nonInteractive: 'no',
      });

      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    setQuery: (value?: string) => {
      if (value) {
        searchParams.set('query', value);
      } else {
        searchParams.delete('query');
      }

      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    setSort: (value: SortValue) => {
      searchParams.set('sort', value);
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
  };
}
